/* TypeScript DTOs for POD (Predictive Ordering & Demand) API */

/* ---- Core client infrastructure ---- */
export interface ClientConfig {
  baseUrl?: string;
  defaultHeaders?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(public status: number, public responseBody: string) {
    super(`API Error ${status}: ${responseBody}`);
  }
}

/* ---- DTOs ---- */

/** Generic response with status information */
export interface DetailedResponse {
  status: string;
}

/** Item conversion configuration for frozen/non-frozen items */
export interface ItemConversion {
  itemNumber: string;
  conversionNumber: number;
  isFrozen?: boolean;
}

/** Store number mapping between internal and Starbucks systems */
export interface FranchiseStore {
  storeNumber: string;
  starbucksStoreNumber: string;
}

/** POD configuration for an item at a specific store/franchise level */
export interface PodConfigurationModel {
  id: number;
  storeNumber: string;
  storeName: string;
  starbucksStoreNumber: string;
  itemNumber: string;
  itemDescription: string;
  franchiseName: string;
  minStockOnHandQuantity: number | string | null;
  maxStockOnHandQuantity: number | string | null;
  safetyStock: number | string | null;
  isParEnabled: boolean;
  configurationLevel: number;
  enabled: boolean;
  franchiseStores: FranchiseStore[];
}

/** Predicted demand for an item with prediction details */
export interface PredictedDemandModel {
  itemNumber: string;
  predictionInfo: PredictionInfo | null;
  predictedDemandItems: PredictedDemandItemModel[];
}

/** Individual predicted demand entry for a specific date */
export interface PredictedDemandItemModel {
  quantity: number;
  date: Date;
  hasPrediction: boolean;
  predictedDemandQuantity: number;
  safetyStock: number | null;
}

/** Prediction metadata (extensible) */
export interface PredictionInfo {
  // Reserved for future prediction metadata
}

/** Store-level POD configuration */
export interface StoreConfiguration {
  isPodEnabled: boolean;
}

/* ---- Request Types ---- */

/** Request parameters for getting predictions for specific items */
export interface GetPredictionsForItemsRequest {
  franchiseName: string;
  storeNumber: string;
  dates: string[];
  itemNumbers: string[];
  conversions: ItemConversion[];
}

/* ---- Configuration Levels ---- */

/** Configuration level enum for POD settings */
export enum PodConfigurationLevel {
  /** Global default configuration */
  Global = 0,
  /** Franchise-level configuration */
  Franchise = 1,
  /** Store-level configuration */
  Store = 2,
  /** Item-level configuration at a store */
  Item = 3,
}
